import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getFreelancerProposalDocuments,
  uploadFreelancerProposalDocument,
  deleteFreelancerProposalDocument
} from '../api/freelancer-proposals-api';
import { FreelancerProposalDocument } from '../models/FreelancerProposalDocument';
import { useSnackbarContext } from '../contexts/useSnackBarContext';
import { useTranslation } from 'react-i18next';

/**
 * Hook to fetch freelancer proposal documents
 */
export const useFreelancerProposalDocuments = (proposalId: number) => {
  return useQuery({
    queryKey: ['freelancerProposalDocuments', proposalId],
    queryFn: () => getFreelancerProposalDocuments(proposalId),
    enabled: proposalId > 0,
    staleTime: 0, 
    gcTime: 0,
  });
};

/**
 * Hook to upload a freelancer proposal document
 */
export const useUploadFreelancerProposalDocument = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showSnackbar } = useSnackbarContext();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      proposalId,
      document
    }: {
      proposalId: number;
      document: { name: string; content: string }
    }) => uploadFreelancerProposalDocument(proposalId, document),

    onSuccess: (data, variables) => {
      // Update the documents list
      queryClient.setQueryData<FreelancerProposalDocument[]>(
        ['freelancerProposalDocuments', variables.proposalId],
        (old = []) => [data, ...old]
      );

      showSuccess('Document ajouté avec succès');
    },

    onError: (error: any) => {
      const message = error.response?.data?.message || t('Erreur lors de l\'ajout du document');
      showSnackbar({ message, severity: 'error' });
    }
  });
};

/**
 * Hook to delete a freelancer proposal document
 */
export const useDeleteFreelancerProposalDocument = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showSnackbar } = useSnackbarContext();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      proposalId,
      documentId
    }: {
      proposalId: number;
      documentId: number
    }) => deleteFreelancerProposalDocument(proposalId, documentId),

    onSuccess: (_, variables) => {
      // Remove the document from the list
      queryClient.setQueryData<FreelancerProposalDocument[]>(
        ['freelancerProposalDocuments', variables.proposalId],
        (old = []) => old.filter(doc => doc.id !== variables.documentId)
      );

      showSuccess('Document supprimé avec succès');
    },

    onError: (error: any) => {
      const message = error.response?.data?.message || t('Erreur lors de la suppression du document');
      showSnackbar({ message, severity: 'error' });
    }
  });
};